import type { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import UserAvatar, { type UserAvatarProps } from '@/components/common/user-carousel/user-avatar';
import ROUTES from '@/routes';
import { useUserId } from '@/stores/auth';
import { addToast } from '@/stores/toast';
import cn from '@/utils/merge-class-names';

export type UserCarouselItem = UserAvatarProps & { userId: number; groupId?: number };

interface Props {
  users: Array<UserCarouselItem>;
  withLink?: boolean;
  className?: string;
  disableRemoveUserIds?: number[];
  onRemove?: (userId: number) => void;
}

export function UserCarousel({
  users,
  withLink,
  children,
  className,
  disableRemoveUserIds,
  onRemove,
}: PropsWithChildren<Props>) {
  const { t } = useTranslation();
  const selfUserId = useUserId();

  const handleClickSelf = () => {
    addToast({
      type: 'info',
      message: t('conversation.setting.group.admin.operate-self'),
    });
  };

  const handleRemove = (userId: number) => {
    if (!onRemove || disableRemoveUserIds?.includes(userId)) {
      return undefined;
    }
    return () => onRemove(userId);
  };

  return (
    <div className={cn('flex gap-x-2 overflow-x-auto overflow-y-hidden my-4 min-h-20', className)}>
      {users.map((user) =>
        withLink ? (
          user.userId === selfUserId ? (
            <button
              key={`user-carousel-${user.userId}`}
              type='button'
              onClick={handleClickSelf}>
              <UserAvatar
                className='w-16 overflow-hidden px-1'
                {...user}
                onRemove={handleRemove(user.userId)}
              />
            </button>
          ) : (
            <Link
              key={`user-carousel-${user.userId}`}
              to={ROUTES.CONTACT.USERS.BY_ID.$buildPath({
                params: { userId: user.userId },
                searchParams: { groupId: user.groupId },
              })}>
              <UserAvatar
                {...user}
                className='w-16 overflow-hidden px-1'
                onRemove={handleRemove(user.userId)}
              />
            </Link>
          )
        ) : (
          <UserAvatar
            className='w-16 overflow-hidden px-1'
            key={user.userId}
            {...user}
            onRemove={user.userId === selfUserId ? undefined : handleRemove(user.userId)}
          />
        ),
      )}

      {children}
    </div>
  );
}
